import {
  addTraveller,
  deleteTraveller,
  live,
  updateTraveller,
  type Plan,
  type Traveller,
} from '@trippath/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useReadOnly } from '../../app/readOnly';
import { TravellerDialog } from './TravellerDialog';

/** Where a fellow traveller joins: the stop the trip is at on their first day together. */
export function joinsAt(plan: Plan, traveller: Traveller): string | null {
  const stop = plan.stops.find((s) => s.start <= traveller.joins && traveller.joins < s.end);
  return stop ? stop.city : null;
}

interface Props {
  plan: Plan;
  onSaved: (plan: Plan) => void;
}

/** The tickets a traveller is named on, by their name: the ways they go with you. */
function ticketsOf(plan: Plan, traveller: Traveller): number {
  return plan.stops
    .flatMap((stop) => stop.bookings)
    .filter(
      (b) =>
        b.kind === 'ticket' && live(b) && b.passengers.some((p) => p.name === traveller.name),
    ).length;
}

/**
 * Who travels with you without an account: names to put on a ticket, each with the tickets they
 * are on.
 */
export function Travellers({ plan, onSaved }: Props) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState<Traveller | 'new' | null>(null);
  // A trip read only: who travels is said, no one added or changed.
  const locked = useReadOnly();
  const close = () => {
    setEditing(null);
  };
  const saved = (next: Plan) => {
    setEditing(null);
    onSaved(next);
  };

  return (
    <section className="panel travellers" aria-labelledby="travellers-title">
      <div className="panel-head">
        <h2 id="travellers-title">{t('travellers.title')}</h2>
        {!locked && (
          <button
            type="button"
            className="button"
            onClick={() => {
              setEditing('new');
            }}
          >
            {t('travellers.add')}
          </button>
        )}
      </div>
      {plan.travellers.length === 0 ? (
        <p className="quiet">{t('travellers.none')}</p>
      ) : (
        <ul className="traveller-list">
          {plan.travellers.map((traveller) => {
            const tickets = ticketsOf(plan, traveller);
            return (
              <li key={traveller.id}>
                <span>
                  <strong>{traveller.name}</strong>
                  <span className="quiet">
                    {' · '}
                    {tickets === 0
                      ? t('travellers.noTicket')
                      : t('travellers.onTickets', { count: tickets })}
                  </span>
                </span>
                {!locked && (
                  <button
                    type="button"
                    className="button button-small"
                    aria-label={t('travellers.edit', { name: traveller.name })}
                    onClick={() => {
                      setEditing(traveller);
                    }}
                  >
                    {t('travellers.editShort')}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {editing === 'new' && (
        <TravellerDialog
          plan={plan}
          save={(input) => addTraveller(plan.trip.id, input)}
          onClose={close}
          onSaved={saved}
        />
      )}
      {editing !== null && editing !== 'new' && (
        <TravellerDialog
          plan={plan}
          traveller={editing}
          save={(input) => updateTraveller(plan.trip.id, editing.id, input)}
          remove={() => deleteTraveller(plan.trip.id, editing.id)}
          onClose={close}
          onSaved={saved}
        />
      )}
    </section>
  );
}
